import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import cl from './BaseSimpleItemBrowser.module.css'
import {Alert, LinearProgress, useTheme} from "@mui/material";
import {FixedSizeList} from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import BaseSimpleItemBrowserHeader from "./BaseSimpleItemBrowserHeader";
import BaseSimpleItemBrowserItem from "./BaseSimpleItemBrowserItem";
import ServerManager from "../../API/ServerManager";

const ITEMS_PER_RQ_PAGE = 30;
const TYPING_TIMEOUT = 500; // [ms]

const BaseSimpleItemBrowser = forwardRef(({
                                                modeApiName2,
                                                filteredItems,
                                                setFilteredItems,
                                                refsData,
                                                setRefsData,
                                                selectedItemId,
                                                setSelectedItemId,
                                                setSearchByIdMode,
                                                style,
                                                renderHeaderFunc,
                                                getItemTitleFunc,
                                                getItemSubtitleFunc,
                                                getItemUptitleFunc,
                                                filterExtraParams = {},
                                                isProcessingFilter, //??
                                                onUpdateItemFunc, //??
                                                searchPlaceholder
                                            }, ref) => {

    const definedRenderHeaderFunc = renderHeaderFunc ?? renderHeader;

    const theme = useTheme();

    const [totalItemCount, setTotalItemCount] = useState(-1);
    const [sortType, setSortType] = useState('id'); // id|name
    const [sortDir, setSortDir] = useState('asc'); // asc|desc

    const [searchText, setSearchText] = useState(''); // строка поиска (поле ввода)
    const [searchCounter, setSearchCounter] = useState(0);

    //----

    const rqIdCounter = useRef(0);
    const [status, setStatus] = useState(""); // "", idle, mainLoad, addLoad, mainError, typing
    const [mainErrorMessage, setMainErrorMessage] = useState("");
    const [lastTypingTime, setLastTypingTime] = useState(0);
    const [pagination, setPagination] = useState({is_last: true})
    const [refreshCounter, setRefreshCounter] = useState(0);


    // for call
    function onFilterEdit(forceUpdate = false) {
        setStatus("typing");
        setLastTypingTime(forceUpdate ? 0 : Date.now());
        setFilteredItems([]);
        setTotalItemCount(-1);
        rqIdCounter.current++;
    }

    useImperativeHandle(ref, () => {
        return {
            /*async updateItem(itemId, filteredItems, setFilteredItems) {
                const itemData = await ServerManager.getSimpleModeListItem(modeApiName2, itemId);
                const wasIndex = filteredItems.findIndex(x => x.id === itemId);
                if (wasIndex >= 0) {
                    const newFilteredItems = [...filteredItems];
                    newFilteredItems[wasIndex] = itemData;
                    setFilteredItems(newFilteredItems);
                }
                if (onUpdateItemFunc)
                    onUpdateItemFunc();
            },*/
            refreshItems() {
                setRefreshCounter(x => x + 1);
            },
            onFilterEdit(forceUpdate = false) {
                onFilterEdit(forceUpdate);
            }
        };
    }, []);

    // после первого рендера сразу запрашиваем первую порцию
    useEffect(() => {
        requestMainItems();

        // при анмоунте отменяем текущий запрос
        return () => {
            rqIdCounter.current++;
        }
    }, []);

    // for refresh
    useEffect(() => {
        if (refreshCounter > 0)
            requestMainItems();
    }, [refreshCounter]);


    async function requestMainItems() {
        rqIdCounter.current++;
        const rqId = rqIdCounter.current;
        //console.log("rqId", rqId);

        setStatus("mainLoad")
        try {
            //await new Promise(resolve => setTimeout(resolve, 3500)); // temp!
            const data = await ServerManager.getSimpleModeFilteredList(modeApiName2, ITEMS_PER_RQ_PAGE, searchText, sortType, sortDir, 0, filterExtraParams);
            if (rqIdCounter.current === rqId) {
                setFilteredItems(data[modeApiName2]);
                setRefsData(data.refs);
                setTotalItemCount(data.total);
                setPagination(data.pagination);
                setStatus("idle");
            }
        } catch (error) {
            if (rqIdCounter.current === rqId) {
                setMainErrorMessage(`Ошибка при загрузке данных: ${error}`);
                setStatus("mainError");
            }
        } finally {
            if (rqIdCounter.current !== rqId) {
                //console.log("ignored! rqId", rqId);
            }
        }
    }

    async function requestMoreItems() {
        rqIdCounter.current++;
        const rqId = rqIdCounter.current;
        //console.log("rqId", rqId);

        setStatus("addLoad")
        try {
            //await new Promise(resolve => setTimeout(resolve, 3500)); // temp!
            const data = await ServerManager.getSimpleModeFilteredList(modeApiName2, ITEMS_PER_RQ_PAGE, searchText, sortType, sortDir, pagination.next_offset, filterExtraParams);
            if (rqIdCounter.current === rqId) {
                setFilteredItems([...filteredItems, ...data[modeApiName2]]);
                setRefsData(mergeRefDatas(refsData, data.refs));
                setPagination(data.pagination);
                setStatus("idle");
            }
        } catch (error) {
            if (rqIdCounter.current === rqId) {
                //setMainErrorMessage(`Ошибка при загрузке данных: ${error}`); // TODO popup?
                setStatus("idle");
            }
        } finally {
            if (rqIdCounter.current !== rqId) {
                //console.log("ignored! rqId", rqId);
            }
        }
    }

    // запуск/продление временной задержки во время ввода текста
    useEffect(() => {
        async function wait() {
            const leftTime = lastTypingTime + TYPING_TIMEOUT - Date.now();
            if (leftTime > 0)
                await new Promise(resolve => setTimeout(resolve, leftTime));
            if (!ignore)
                requestMainItems();
        }

        if (status !== "typing")
            return;

        let ignore = false;
        wait();
        return () => {
            ignore = true;
        }

    }, [status, lastTypingTime]);

    function renderListRow({index, style, data}) {

        const item = data[index];

        return (
            <BaseSimpleItemBrowserItem
                style={style}
                id={item.id}
                title={getItemTitleFunc ? getItemTitleFunc(item) : item.name}
                subtitle={getItemSubtitleFunc ? getItemSubtitleFunc(item) : ""}
                uptitle={getItemUptitleFunc ? getItemUptitleFunc(item) : ""}
                selected={item.id === selectedItemId}
                onClick={() => {
                    setSearchByIdMode(false);
                    setSelectedItemId(item.id);
                }}
            />
        );
    }

    function getListItemKey(index, data) {
        return data[index].id;
    }

    function renderHeader(headerProps) {
        return <BaseSimpleItemBrowserHeader {...headerProps}/>
    }

    function onSearchById(id) {
        setSearchByIdMode(true);
        setSelectedItemId(id);
    }

    return (<div className={cl.main} style={style}>
        {/*<p>Status: {status}</p>*/}
        {definedRenderHeaderFunc({
            totalItemCount: totalItemCount,
            sortType: sortType,
            setSortType: setSortType,
            sortDir: sortDir,
            setSortDir: setSortDir,
            searchText: searchText,
            setSearchText: setSearchText,
            theme: theme,
            onFilterEdit: onFilterEdit,
            searchPlaceholder: searchPlaceholder,
            onSearchById: onSearchById
        })}
        {(status === "mainLoad" || status === "typing") && <LinearProgress/>}
        {status === "mainError" && <Alert variant="outlined" severity="error">{mainErrorMessage}</Alert>}
        {filteredItems.length > 0 &&
            <div className={cl.itemList}>
                <MyList
                    filteredItems={filteredItems}
                    getListItemKey={getListItemKey}
                    renderListRow={renderListRow}
                    selectedItemId={selectedItemId}
                    sortType={sortType}
                    requestMoreItems={requestMoreItems}
                    pagination={status === "idle" ? pagination : null}
                    refreshCounter={refreshCounter}
                />
            </div>}
    </div>);
});

const MyList = ({
                    filteredItems,
                    getListItemKey,
                    renderListRow,
                    selectedItemId,
                    sortType,
                    requestMoreItems,
                    pagination,
                    refreshCounter
                }) => {

    const listRef = useCallback(node => {
        //console.log(">>> cb", "node=", node)
        if (node !== null) {
            if (selectedItemId) {
                const item = filteredItems.find(x => x.id === selectedItemId);
                if (item) {
                    node.scrollToItem(filteredItems.indexOf(item), "smart");
                }
            }
        }
        //}, [filteredItems, selectedItemId, sortType, refreshCounter]);
    }, [selectedItemId, sortType, refreshCounter]);

    return <AutoSizer>
        {({height, width}) => (
            <FixedSizeList
                ref={listRef}
                width={width}
                height={height}
                itemCount={filteredItems.length}
                itemSize={110}
                overscanCount={150}
                itemData={filteredItems}
                itemKey={getListItemKey}
                onItemsRendered={({visibleStopIndex}) => {
                    if (pagination && !pagination.is_last && visibleStopIndex + 1 >= filteredItems.length) {
                        requestMoreItems();
                    }
                }}
            >
                {renderListRow}
            </FixedSizeList>)}
    </AutoSizer>
}

//------

// tool
function mergeRefDatas(refData1, refData2) {
    if (!refData1)
        return refData2;

    if (!refData2)
        return refData1;

    return  deepMerge(refData1, refData2);
}

// tool (from internet)
// TODO! check!
function deepMerge(target, source) {
    // Create a deep copy of the target to avoid mutation of the original object
    const output = {...target};

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key]) && target[key]) {
                // If both are objects, merge recursively
                output[key] = deepMerge(target[key], source[key]);
            } else {
                // Otherwise, use the source value (overwrites target's primitives/arrays)
                output[key] = source[key];
            }
        }
    }

    return output;
}

// tool (from internet)
function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}

export default BaseSimpleItemBrowser;